/**
 * The header's four destinations below 768px, behind one button.
 *
 * The desktop header is a single row — wordmark, `Markets Portfolio History
 * Leaderboard`, then balance, bell and account — and on a phone that row is
 * about 650px of content in 375px of screen. Nothing in it could shrink, so it
 * did not clip: it widened the document, and iOS Safari **pans** a document
 * wider than the screen regardless of `body { overflow: hidden }`. The whole
 * market page slid sideways and every panel lost its left edge. The four links
 * are the part of that row a trader needs least often, so they are the part
 * that folds.
 *
 * **A disclosure, not a menu.** `role="menu"` promises arrow-key navigation and
 * a roving focus (invariant 20 — a `role` is a promise about the keyboard), and
 * a list of four page links is not that widget. So it is a button that says
 * whether it is expanded and a `<nav>` of ordinary links: Tab walks them, Enter
 * follows one, and Escape puts focus back on the button that opened it.
 *
 * **Open is derived from the route, not only set.** The state records *which
 * page* the panel was opened on, and it is open only while that is still the
 * page — so any navigation closes it, including Back and a notification's deep
 * link, with no effect watching the location. Tapping the link to the page you
 * are already on changes nothing about the route, which is why each link also
 * closes it directly.
 */

import { useEffect, useId, useRef, useState } from 'react'
import { NavLink, useLocation } from 'react-router-dom'
import { cx } from '@/lib/cx'
import type { NavItem } from './Header'

export interface PhoneNavProps {
  items: readonly NavItem[]
  className?: string
}

export function PhoneNav({ items, className }: PhoneNavProps) {
  const { pathname } = useLocation()
  const [openOn, setOpenOn] = useState<string | null>(null)
  const open = openOn === pathname
  const panelId = useId()
  // The trigger **and** the panel. A dismisser that tested the panel alone
  // would close on the trigger's own pointerdown and reopen on its click, so a
  // tap on the open button could never close it — `<AccountMenu>`'s shape.
  const wrapper = useRef<HTMLDivElement>(null)
  const trigger = useRef<HTMLButtonElement>(null)

  useEffect(() => {
    if (!open) return
    const onPointerDown = (event: PointerEvent) => {
      if (!wrapper.current?.contains(event.target as Node)) setOpenOn(null)
    }
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return
      setOpenOn(null)
      trigger.current?.focus()
    }
    document.addEventListener('pointerdown', onPointerDown)
    document.addEventListener('keydown', onKeyDown)
    return () => {
      document.removeEventListener('pointerdown', onPointerDown)
      document.removeEventListener('keydown', onKeyDown)
    }
  }, [open])

  return (
    // **No `relative` here, deliberately.** The panel is positioned against
    // `<Header>`, so it spans the full width of the screen under it rather than
    // the 44px of this wrapper.
    <div ref={wrapper} className={cx('md:hidden', className)}>
      <button
        ref={trigger}
        type="button"
        aria-label="Menu"
        aria-expanded={open}
        aria-controls={panelId}
        onClick={() => setOpenOn(open ? null : pathname)}
        className={cx(
          'flex min-h-[var(--ud-tap-min)] min-w-[var(--ud-tap-min)] items-center justify-center rounded-sm',
          'text-text-secondary hover:text-text-primary',
          'focus-visible:shadow-focus focus-visible:outline-none',
        )}
      >
        {/* Drawn rather than typed, for the bell's reason: the six glyphs this
            product ships live in the mono face (§F17), and `☰` is not one. */}
        <svg viewBox="0 0 16 16" className="h-4 w-4" fill="none" aria-hidden="true">
          <path d="M2.5 4h11M2.5 8h11M2.5 12h11" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" />
        </svg>
      </button>

      {/* Always rendered, `hidden` while closed, so `aria-controls` names an
          element that exists. */}
      <nav
        id={panelId}
        aria-label="primary"
        hidden={!open}
        className="absolute inset-x-0 top-full z-30 flex flex-col border-b border-border-default bg-bg-surface py-1 shadow-popover"
      >
        {items.map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            onClick={() => setOpenOn(null)}
            className={({ isActive }) =>
              cx(
                'flex min-h-[var(--ud-tap-min)] items-center px-3 text-body',
                'focus-visible:shadow-focus focus-visible:outline-none',
                isActive ? 'bg-bg-active text-text-primary' : 'text-text-secondary hover:bg-bg-hover',
              )
            }
          >
            {item.label}
          </NavLink>
        ))}
      </nav>
    </div>
  )
}
